import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";

function buildInitialForm(fields) {
  return fields.reduce((accumulator, field) => {
    accumulator[field.name] = "";
    return accumulator;
  }, {});
}

function LookupFormPage({ title, endpoint, fields, listPath }) {
  const history = useHistory();
  const { uuid } = useParams();
  const isEditing = Boolean(uuid);
  const [form, setForm] = useState(() => buildInitialForm(fields));
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isEditing) {
      fetchItem();
    }
  }, [uuid]);

  const submitLabel = useMemo(() => (isEditing ? `Update ${title}` : `Create ${title}`), [isEditing, title]);

  async function fetchItem() {
    setLoading(true);
    setError("");

    try {
      const response = await api.get(`${endpoint}/${uuid}`);
      const item = response.data || {};
      setForm(
        fields.reduce((accumulator, field) => {
          accumulator[field.name] = item[field.name] || "";
          return accumulator;
        }, {})
      );
    } catch (err) {
      setError(err.response?.data?.error || `Failed to load ${title.toLowerCase()}.`);
    } finally {
      setLoading(false);
    }
  }

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);

    try {
      if (isEditing) {
        await api.put(`${endpoint}/${uuid}`, form);
        toast.success(`${title} updated`);
      } else {
        await api.post(endpoint, form);
        toast.success(`${title} created`);
      }

      history.push(listPath);
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to save ${title.toLowerCase()}.`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Row className="justify-content-center">
      <Col xs={12} xl={10}>
        <PageHeader
          title={isEditing ? `Edit ${title}` : `Add ${title}`}
          action={
            <Button variant="outline-secondary" onClick={() => history.push(listPath)}>
              Back to {title.toLowerCase()}
            </Button>
          }
        />

        {loading ? <LoadingState label={`Loading ${title.toLowerCase()}...`} /> : null}
        {error ? <Alert variant="danger">{error}</Alert> : null}

        {!loading ? (
          <div className="content-card form-card">
            <form onSubmit={handleSubmit}>
              {fields.map((field) => (
                <div className="mb-3" key={field.name}>
                  <label className="form-label">{field.label}</label>
                  {field.type === "textarea" ? (
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name={field.name}
                      value={form[field.name]}
                      onChange={handleChange}
                      required={field.required !== false}
                    />
                  ) : (
                    <Form.Control
                      type={field.type || "text"}
                      name={field.name}
                      value={form[field.name]}
                      onChange={handleChange}
                      required={field.required !== false}
                    />
                  )}
                </div>
              ))}

              <div className="d-flex gap-2 mt-4">
                <Button type="submit" disabled={saving}>
                  {saving ? "Saving..." : submitLabel}
                </Button>
                <Button variant="outline-secondary" onClick={() => history.push(listPath)}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        ) : null}
      </Col>
    </Row>
  );
}

export default LookupFormPage;
